import React, { useState, useContext } from "react";
import {
  Grid,
  Typography,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Button as MuiButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTitle } from "src/hooks/useTitle";
import { ProductRow } from "src/pages/Messaging/components/ProductRow";
import { SearchResult } from "src/models/search";
import { postProductSyncFromProdAsync } from "src/infrastructure/http/appApiService";
import PageContext from "src/components/Context/PageContext";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { ProductionSearchField } from "./components/ProductionSearchField";
import { useCountry } from "src/components/Context/CountryContext";

export const ProductProdToUATSyncPage: React.FC = () => {
  useTitle("The Replicator");

  const [searchValue, setSearchValue] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<SearchResult | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const { country } = useCountry();

  const [contextData] = useContext(PageContext);
  const { userProfile } = contextData;

  const handleInputChange = (index: number, sku: string, product?: SearchResult) => {
    setSearchValue(sku);
    setSelectedProduct(product || null);
  };

  const handleSync = async () => {
    if (!selectedProduct) return;

    setSyncing(true);
    try {
      const response = await postProductSyncFromProdAsync(selectedProduct.sku, country);
      setResponseMessage(response.message || "SKU synced successfully from Production to UAT");
      setIsSuccess(true);
      setModalOpen(true);
      // Clear search after successful sync
      setSelectedProduct(null);
      setSearchValue("");
    } catch (error: any) {
      setResponseMessage(error?.response?.data?.message || "Failed to sync SKU. Please try again or contact support.");
      setIsSuccess(false);
      setModalOpen(true);
    } finally {
      setSyncing(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setResponseMessage("");
  };

  const handleClear = () => {
    setSelectedProduct(null);
    setSearchValue("");
  };

  const renderSKUOnly = (sku: string, onClear: () => void) => (
    <Box
      sx={{
        ...starWarsStyles.skuBox,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 1,
        py: 1,
      }}
    >
      <Typography sx={starWarsStyles.skuText}>{sku}</Typography>
      <MuiButton onClick={onClear} size="small" sx={{ color: "#ff5252", fontFamily: "'Courier New', monospace" }}>
        Clear
      </MuiButton>
    </Box>
  );

  if (!hasPermission(UserRoles.ProductProdToUATSync, userProfile.roles)) {
    window.location.href = "/";
    return null;
  }

  return (
    <Box sx={starWarsStyles.container}>
      <style>
        {`
          @keyframes starZoom {
            0% {
              transform: scale(0.5) translateZ(0);
              opacity: 0.4;
            }
            10% {
              opacity: 1;
            }
            90% {
              opacity: 0.8;
            }
            100% {
              transform: scale(2.5) translateZ(0);
              opacity: 0;
            }
          }
          @keyframes scan {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}
      </style>
      <Grid container spacing={3} paddingTop={4} sx={{ position: "relative", zIndex: 1 }}>
        <Grid item xs={12}>
          <Box sx={{ maxWidth: "800px", margin: "0 auto" }}>
            <Typography sx={starWarsStyles.title}>THE REPLICATOR</Typography>
            <Typography sx={starWarsStyles.subtitle}>
              "In a galaxy far, far away... product must copy from Production to UAT"
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              ...starWarsStyles.panel,
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            <Typography variant="body1" sx={{ ...starWarsStyles.label, mb: 2 }}>
              &gt; SEARCH FOR PRODUCT SKU FROM PRODUCTION
            </Typography>

            {selectedProduct ? (
              <Box sx={starWarsStyles.productRowWrapper}>
                <ProductRow product={selectedProduct} isAdded={true} onClick={handleClear} />
              </Box>
            ) : searchValue ? (
              renderSKUOnly(searchValue, handleClear)
            ) : (
              <ProductionSearchField
                value={searchValue}
                index={0}
                handleInputChange={handleInputChange}
                placeholder="Enter SKU, PLU, product name, etc."
              />
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              maxWidth: "800px",
              margin: "0 auto",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              onClick={handleClear}
              sx={{
                ...starWarsStyles.clearLink,
                textDecoration: "underline",
                cursor: "pointer",
                visibility: selectedProduct ? "visible" : "hidden",
              }}
            >
              &lt; CLEAR
            </Typography>

            <MuiButton
              onClick={handleSync}
              disabled={!selectedProduct || syncing}
              sx={{
                ...starWarsStyles.button,
                minWidth: "200px",
                color: "white",
                px: 3,
                py: 1,
              }}
            >
              {syncing ? "⚡ SYNCING..." : "⚡ ENGAGE REPLICATOR"}
            </MuiButton>
          </Box>
        </Grid>

        {/* Success/Failure Modal */}
        <Dialog
          open={modalOpen}
          onClose={handleCloseModal}
          PaperProps={{
            sx: {
              minWidth: 400,
              maxWidth: 600,
              position: "relative",
              background: "linear-gradient(135deg, rgba(30, 30, 50, 0.98) 0%, rgba(20, 20, 40, 0.98) 100%)",
              border: `2px solid ${isSuccess ? "#4FC3F7" : "#ff5252"}`,
              boxShadow: `0 0 30px ${isSuccess ? "rgba(79, 195, 247, 0.5)" : "rgba(255, 82, 82, 0.5)"}`,
            },
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#FFE81F",
              "&:hover": {
                color: "#FFF",
              },
            }}
          >
            <Close />
          </IconButton>
          <DialogContent sx={{ pt: 4, pb: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                gutterBottom
                sx={{
                  fontFamily: "'Courier New', monospace",
                  color: isSuccess ? "#4FC3F7" : "#ff5252",
                  textShadow: isSuccess ? "0 0 10px #4FC3F7" : "0 0 10px #ff5252",
                  letterSpacing: "0.1em",
                }}
              >
                {isSuccess ? "✓ MISSION ACCOMPLISHED" : "⚠ SYSTEM MALFUNCTION"}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  fontFamily: "'Courier New', monospace",
                  color: "#4FC3F7",
                  lineHeight: 1.6,
                }}
              >
                {responseMessage}
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Loading overlay for sync */}
        {syncing && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 9999,
            }}
          >
            <CircularProgress
              size={60}
              sx={{
                color: "#4FC3F7",
                filter: "drop-shadow(0 0 10px #4FC3F7)",
              }}
            />
            <Typography
              sx={{
                mt: 3,
                fontFamily: "'Courier New', monospace",
                color: "#FFE81F",
                fontSize: "1.2rem",
                textShadow: "0 0 10px #FFE81F",
                letterSpacing: "0.1em",
              }}
            >
              TRANSMITTING DATA...
            </Typography>
          </Box>
        )}
      </Grid>
    </Box>
  );
};

const starWarsStyles = {
  container: {
    background: "#000000",
    minHeight: "100vh",
    width: "100vw",
    position: "relative" as const,
    marginLeft: "calc(-50vw + 50%)",
    marginRight: "calc(-50vw + 50%)",
    marginTop: "-110px",
    marginBottom: "-20px",
    paddingLeft: "calc(50vw - 50%)",
    paddingRight: "calc(50vw - 50%)",
    paddingTop: "110px",
    paddingBottom: "20px",
    "@media (max-width: 960px)": {
      marginTop: "-80px",
      marginBottom: "-10px",
      paddingTop: "80px",
      paddingBottom: "10px",
    },
    "@media (max-width: 600px)": {
      marginTop: "-70px",
      marginBottom: "-10px",
      paddingTop: "70px",
      paddingBottom: "10px",
    },
    "&::before": {
      content: '""',
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `radial-gradient(2px 2px at 20% 30%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 60% 70%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 50% 50%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 80% 10%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 90% 60%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 33% 80%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 15% 90%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 25% 40%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 70% 30%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 40% 15%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 85% 85%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 5% 60%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 95% 25%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 45% 95%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 10% 20%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 65% 45%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 30% 60%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 75% 75%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 55% 5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 20% 85%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 88% 50%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 12% 65%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 42% 22%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 78% 88%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 8% 44%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 92% 12%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 28% 72%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 62% 38%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 18% 8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 52% 68%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 35% 52%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 72% 18%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 48% 82%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 82% 42%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 14% 78%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 58% 28%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 22% 58%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 68% 92%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 38% 32%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 84% 64%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 26% 14%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 74% 54%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 44% 74%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 86% 26%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 32% 46%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 64% 84%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 16% 36%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 54% 16%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 76% 66%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 24% 96%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 94% 34%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 46% 56%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 6% 76%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 66% 6%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 36% 86%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 56% 24%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 96% 48%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 11% 55%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 71% 11%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 41% 71%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 81% 91%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 51% 41%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 21% 61%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 91% 21%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 31% 81%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 61% 31%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 7% 51%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 77% 7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 47% 77%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 17% 27%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 87% 47%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 27% 67%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 97% 87%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 37% 17%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 67% 37%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 13% 97%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 83% 13%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 53% 63%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 23% 33%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 93% 73%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 43% 43%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 73% 93%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 3% 23%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 63% 53%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 33% 3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 19% 49%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 89% 69%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 49% 89%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 29% 19%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 69% 79%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 39% 39%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 99% 59%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 59% 9%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 9% 99%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 79% 29%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 1% 89%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 34% 64%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 64% 14%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 4% 34%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 74% 44%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 44% 94%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 84% 24%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 54% 54%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 24% 74%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 94% 4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 15% 45%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 85% 15%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 55% 85%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 25% 55%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 95% 95%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 65% 25%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 35% 65%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 5% 35%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 75% 5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 45% 75%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 21.5% 32.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 58.3% 67.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 48.7% 51.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 78.9% 12.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 88.5% 58.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 35.2% 78.9%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 16.7% 88.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 27.3% 42.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 68.2% 32.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 42.8% 17.6%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 83.1% 83.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 7.4% 62.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 92.6% 27.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 47.3% 93.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 12.1% 22.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 63.8% 47.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 32.4% 58.9%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 73.6% 77.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 57.2% 7.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 22.7% 83.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 86.4% 52.6%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 14.3% 63.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 44.5% 24.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 76.1% 86.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 9.7% 46.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 90.3% 14.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 30.8% 70.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 60.5% 40.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 20.2% 10.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 50.9% 66.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 37.6% 50.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 70.4% 20.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 46.1% 80.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 80.8% 44.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 16.2% 76.9%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 56.7% 30.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 24.9% 56.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 66.3% 90.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 40.7% 34.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 82.5% 62.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 28.4% 16.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 72.1% 56.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 42.3% 72.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 84.7% 28.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 34.1% 48.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 62.8% 82.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 18.5% 38.6%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 52.3% 18.9%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 74.8% 64.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 26.6% 94.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 92.1% 36.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 48.9% 54.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 8.3% 74.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 64.1% 8.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 38.2% 84.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 54.8% 26.9%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 94.6% 46.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 13.2% 57.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 69.4% 13.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 43.7% 69.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 79.3% 89.6%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 49.1% 39.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 23.8% 59.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 89.2% 23.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 33.6% 79.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 59.8% 33.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 9.1% 49.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 75.4% 9.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 45.2% 75.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 19.6% 29.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 85.3% 45.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 29.7% 65.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 95.8% 85.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 39.4% 19.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 65.7% 35.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 15.3% 95.6%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 81.7% 15.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 51.4% 61.5%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 25.1% 31.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 91.6% 71.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 41.8% 41.6%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 71.2% 91.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 5.7% 21.9%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 61.9% 51.7%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 31.4% 5.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 17.8% 47.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 87.1% 67.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 47.5% 87.6%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 27.9% 17.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 67.8% 77.6%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 37.1% 37.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 97.4% 57.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 57.6% 11.3%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 11.8% 97.2%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 77.5% 27.4%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(3px 3px at 3.2% 87.9%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.9) 30%, transparent),
                        radial-gradient(2px 2px at 36.7% 62.8%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent),
                        radial-gradient(2px 2px at 62.4% 16.1%, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.8) 40%, transparent)`,
      backgroundSize: "100% 100%",
      animation: "starZoom 2s linear infinite",
      pointerEvents: "none" as const,
      zIndex: 0,
    },
    "&::after": {
      content: '""',
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `radial-gradient(1px 1px at 95% 25%, white, transparent),
                        radial-gradient(2px 2px at 45% 95%, white, transparent),
                        radial-gradient(1px 1px at 10% 20%, white, transparent),
                        radial-gradient(1px 1px at 65% 45%, white, transparent),
                        radial-gradient(2px 2px at 30% 60%, white, transparent),
                        radial-gradient(1px 1px at 75% 75%, white, transparent),
                        radial-gradient(1px 1px at 55% 5%, white, transparent),
                        radial-gradient(2px 2px at 20% 85%, white, transparent),
                        radial-gradient(1px 1px at 88% 50%, white, transparent),
                        radial-gradient(1px 1px at 12% 65%, white, transparent),
                        radial-gradient(1px 1px at 42% 12%, white, transparent),
                        radial-gradient(2px 2px at 72% 52%, white, transparent),
                        radial-gradient(1px 1px at 28% 82%, white, transparent),
                        radial-gradient(1px 1px at 58% 32%, white, transparent),
                        radial-gradient(1px 1px at 8% 72%, white, transparent),
                        radial-gradient(2px 2px at 82% 22%, white, transparent),
                        radial-gradient(1px 1px at 38% 62%, white, transparent),
                        radial-gradient(1px 1px at 68% 92%, white, transparent),
                        radial-gradient(1px 1px at 18% 42%, white, transparent),
                        radial-gradient(2px 2px at 92% 8%, white, transparent),
                        radial-gradient(1px 1px at 52% 38%, white, transparent),
                        radial-gradient(1px 1px at 22% 78%, white, transparent),
                        radial-gradient(1px 1px at 62% 18%, white, transparent),
                        radial-gradient(2px 2px at 32% 58%, white, transparent),
                        radial-gradient(1px 1px at 78% 88%, white, transparent),
                        radial-gradient(1px 1px at 48% 28%, white, transparent),
                        radial-gradient(1px 1px at 2% 68%, white, transparent),
                        radial-gradient(2px 2px at 86% 38%, white, transparent),
                        radial-gradient(1px 1px at 26% 48%, white, transparent),
                        radial-gradient(1px 1px at 66% 8%, white, transparent),
                        radial-gradient(1px 1px at 36% 88%, white, transparent),
                        radial-gradient(2px 2px at 96% 28%, white, transparent),
                        radial-gradient(1px 1px at 16% 58%, white, transparent),
                        radial-gradient(1px 1px at 76% 98%, white, transparent),
                        radial-gradient(1px 1px at 46% 18%, white, transparent),
                        radial-gradient(2px 2px at 6% 48%, white, transparent),
                        radial-gradient(1px 1px at 56% 78%, white, transparent),
                        radial-gradient(1px 1px at 86% 68%, white, transparent),
                        radial-gradient(1px 1px at 24% 38%, white, transparent),
                        radial-gradient(2px 2px at 74% 8%, white, transparent),
                        radial-gradient(1px 1px at 44% 68%, white, transparent),
                        radial-gradient(1px 1px at 14% 28%, white, transparent),
                        radial-gradient(1px 1px at 84% 58%, white, transparent),
                        radial-gradient(2px 2px at 54% 98%, white, transparent),
                        radial-gradient(1px 1px at 94% 18%, white, transparent),
                        radial-gradient(1px 1px at 34% 78%, white, transparent),
                        radial-gradient(1px 1px at 64% 48%, white, transparent),
                        radial-gradient(2px 2px at 4% 88%, white, transparent),
                        radial-gradient(1px 1px at 70% 14%, white, transparent),
                        radial-gradient(1px 1px at 40% 54%, white, transparent),
                        radial-gradient(1px 1px at 90% 84%, white, transparent),
                        radial-gradient(2px 2px at 50% 44%, white, transparent),
                        radial-gradient(1px 1px at 80% 74%, white, transparent),
                        radial-gradient(1px 1px at 20% 4%, white, transparent),
                        radial-gradient(1px 1px at 60% 64%, white, transparent),
                        radial-gradient(2px 2px at 30% 24%, white, transparent),
                        radial-gradient(1px 1px at 98% 54%, white, transparent),
                        radial-gradient(1px 1px at 8% 94%, white, transparent),
                        radial-gradient(1px 1px at 78% 34%, white, transparent),
                        radial-gradient(2px 2px at 48% 14%, white, transparent),
                        radial-gradient(1px 1px at 88% 94%, white, transparent),
                        radial-gradient(1px 1px at 28% 44%, white, transparent),
                        radial-gradient(1px 1px at 68% 84%, white, transparent),
                        radial-gradient(2px 2px at 38% 4%, white, transparent),
                        radial-gradient(1px 1px at 58% 74%, white, transparent),
                        radial-gradient(1px 1px at 98% 34%, white, transparent),
                        radial-gradient(1px 1px at 18% 64%, white, transparent),
                        radial-gradient(2px 2px at 88% 14%, white, transparent),
                        radial-gradient(1px 1px at 3% 54%, white, transparent),
                        radial-gradient(1px 1px at 73% 24%, white, transparent),
                        radial-gradient(1px 1px at 43% 84%, white, transparent),
                        radial-gradient(2px 2px at 13% 44%, white, transparent),
                        radial-gradient(1px 1px at 83% 4%, white, transparent),
                        radial-gradient(1px 1px at 53% 64%, white, transparent),
                        radial-gradient(1px 1px at 23% 94%, white, transparent),
                        radial-gradient(2px 2px at 93% 54%, white, transparent),
                        radial-gradient(1px 1px at 63% 24%, white, transparent),
                        radial-gradient(1px 1px at 33% 14%, white, transparent),
                        radial-gradient(1px 1px at 7% 84%, white, transparent),
                        radial-gradient(2px 2px at 77% 44%, white, transparent),
                        radial-gradient(1px 1px at 47% 4%, white, transparent),
                        radial-gradient(1px 1px at 17% 74%, white, transparent),
                        radial-gradient(1px 1px at 87% 34%, white, transparent),
                        radial-gradient(2px 2px at 57% 94%, white, transparent),
                        radial-gradient(1px 1px at 27% 54%, white, transparent),
                        radial-gradient(1px 1px at 97% 14%, white, transparent),
                        radial-gradient(1px 1px at 67% 74%, white, transparent),
                        radial-gradient(2px 2px at 37% 44%, white, transparent),
                        radial-gradient(1px 1px at 11% 4%, white, transparent),
                        radial-gradient(1px 1px at 81% 64%, white, transparent),
                        radial-gradient(1px 1px at 51% 34%, white, transparent),
                        radial-gradient(2px 2px at 21% 84%, white, transparent),
                        radial-gradient(1px 1px at 91% 44%, white, transparent),
                        radial-gradient(1px 1px at 61% 4%, white, transparent),
                        radial-gradient(1px 1px at 31% 74%, white, transparent),
                        radial-gradient(2px 2px at 1% 14%, white, transparent),
                        radial-gradient(1px 1px at 71% 54%, white, transparent),
                        radial-gradient(1px 1px at 41% 24%, white, transparent),
                        radial-gradient(1px 1px at 15% 94%, white, transparent),
                        radial-gradient(2px 2px at 85% 84%, white, transparent),
                        radial-gradient(1px 1px at 55% 44%, white, transparent),
                        radial-gradient(1px 1px at 25% 4%, white, transparent),
                        radial-gradient(1px 1px at 95% 64%, white, transparent),
                        radial-gradient(2px 2px at 65% 34%, white, transparent),
                        radial-gradient(1px 1px at 35% 94%, white, transparent),
                        radial-gradient(1px 1px at 5% 24%, white, transparent),
                        radial-gradient(1px 1px at 75% 84%, white, transparent)`,
      backgroundSize: "100% 100%",
      animation: "starZoom 2s linear infinite",
      animationDelay: "0.5s",
      pointerEvents: "none" as const,
      zIndex: 0,
    },
  },
  title: {
    fontFamily: "'Courier New', monospace",
    fontSize: "24rem",
    fontWeight: 700,
    color: "#FFE81F",
    textAlign: "center" as const,
    textShadow: "0 0 15px #FFE81F, 0 0 30px #FFE81F, 0 0 45px #FFE81F, 0 0 60px #FFE81F",
    letterSpacing: "0.15em",
    marginBottom: 2,
  },
  subtitle: {
    fontFamily: "'Courier New', monospace",
    color: "#4FC3F7",
    textAlign: "center" as const,
    fontSize: "1.8rem",
    fontStyle: "italic",
    marginBottom: 4,
    textShadow: "0 0 5px #4FC3F7",
  },
  panel: {
    background: "linear-gradient(135deg, rgba(30, 30, 50, 0.95) 0%, rgba(20, 20, 40, 0.95) 100%)",
    border: "2px solid #4FC3F7",
    borderRadius: "8px",
    padding: 3,
    boxShadow: "0 0 20px rgba(79, 195, 247, 0.3), inset 0 0 20px rgba(79, 195, 247, 0.1)",
    position: "relative" as const,
    "&::before": {
      content: '""',
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      background: "linear-gradient(90deg, transparent, #4FC3F7, transparent)",
      animation: "scan 2s linear infinite",
    },
  },
  label: {
    fontFamily: "'Courier New', monospace",
    color: "#FFE81F",
    fontWeight: 600,
    textShadow: "0 0 5px #FFE81F",
  },
  button: {
    background: "linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)",
    border: "2px solid #4FC3F7",
    boxShadow: "0 0 15px rgba(79, 195, 247, 0.5)",
    fontFamily: "'Courier New', monospace",
    fontWeight: 700,
    letterSpacing: "0.05em",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 0 25px rgba(79, 195, 247, 0.8)",
      transform: "translateY(-2px)",
    },
  },
  clearLink: {
    fontFamily: "'Courier New', monospace",
    color: "#FFE81F",
    textShadow: "0 0 5px #FFE81F",
    "&:hover": {
      color: "#FFF",
      textShadow: "0 0 10px #FFE81F",
    },
  },
  skuBox: {
    background: "rgba(30, 30, 50, 0.8)",
    border: "1px solid #4FC3F7",
    borderRadius: "4px",
    boxShadow: "0 0 10px rgba(79, 195, 247, 0.2)",
  },
  skuText: {
    fontFamily: "'Courier New', monospace",
    color: "#4FC3F7",
    textShadow: "0 0 5px #4FC3F7",
  },
  productRowWrapper: {
    "& *": {
      color: "#FFE81F !important",
    },
    "& strong": {
      color: "#FFE81F !important",
      textShadow: "0 0 5px #FFE81F",
    },
  },
};
